import { emitScan } from '~/utils/scanSource/bus'
import { watch } from 'vue'
import { DEVICE_EVENT, deviceBridge, postToDevice, readDeviceEvent } from '~/utils/scanSource/deviceBridge'
import { applyGunSettings, requestGunStatus } from '~/utils/scanSource/gunCommands'
import { markGunAsking, markGunUnanswered, reportGunStatus } from '~/utils/scanSource/gunStatus'
import { askWifi, reportWifi } from '~/utils/scanSource/wifiSignal'
import { useSyncQueue } from '~/composables/useSyncQueue'
import { sideKeyTarget } from '~/utils/sideKeys'
import { askTabletInfo, reportTabletInfo } from '~/utils/scanSource/tabletInfo'
import { settleCameraScan } from '~/utils/scanSource/cameraScan'
import { settlePrint } from '~/utils/scanSource/printing'
import { tellAppKeepScreenOn } from '~/utils/keepAwake'

/**
 * DockAutomation Tablet Browser scan source.
 *
 * On the forklift tablets this site runs inside our own Android app, which
 * also holds the barcode gun (see utils/scanSource/deviceBridge.ts). Scans and
 * the gun's presence arrive as DOM events and are fanned out on the same bus
 * the screens already subscribe to via useScannerInput() — so no screen knows
 * or cares which browser it is in.
 *
 * Like any scan that arrives whole, these bypass the keyboard source's burst
 * heuristics, ignore minLength, and fire even while an input has focus.
 *
 * It runs only where the app's object exists. Everywhere else — a desktop, a
 * phone — this plugin does nothing at all.
 */

// Module scope: survives plugin re-execution within one boot (dev HMR) so the
// listener is never registered twice.
let started = false

/** How long the app gets to answer a status request before silence counts. */
const GUN_ANSWER_MS = 3000

/**
 * Ask the app whether a gun is held. Silence for three seconds means the part
 * of the app that holds the gun is not running — which, to the driver, is the
 * same as no gun — and the strip says so until a report lands.
 */
function askGun() {
  markGunAsking()
  requestGunStatus()
  window.setTimeout(() => markGunUnanswered('The tablet app did not answer about the gun.'), GUN_ANSWER_MS)
}

export default defineNuxtPlugin(() => {
  if (started || !deviceBridge()) return
  started = true

  // Taken here, where there is a Nuxt context to build it in; the listener
  // below runs outside one.
  const queue = useSyncQueue()
  const router = useRouter()
  /** Whether the last Wi-Fi report had the internet behind it, to notice it coming BACK. */
  let hadInternet: boolean | null = null

  window.addEventListener(DEVICE_EVENT, (event) => {
    const received = (event as CustomEvent).detail
    const read = readDeviceEvent(received)
    // A Wi-Fi reading comes every few seconds for as long as the page is
    // open. Handled before the logging below, which would bury everything
    // else in the console under it.
    if (read?.kind === 'wifi') {
      reportWifi(read)
      // The forklift has driven back into range. The tablet knows it the
      // instant it happens; the site would otherwise find out at its next
      // probe, up to ten seconds later. Only a nudge: the queue asks the API
      // and that answer decides (connectivity.ts).
      const hasInternet = read.connected && read.internet
      if (hasInternet && hadInternet === false) queue.nudge()
      // And out of it: the queue asks the API now, so a scan that cannot be
      // checked without a signal is refused at once instead of after a
      // request's time limit (utils/offlineRules.ts).
      if (!hasInternet && hadInternet === true) queue.noteUnreachable()
      hadInternet = hasInternet
      return
    }
    // Logged raw, every time. Two failures look identical from the fields —
    // the app never sending, and the app sending a shape this rejects — and
    // only the console can tell them apart.
    console.info('[device]', read ?? '(not understood)', received)
    if (!read) return
    if (read.kind === 'gun-status') {
      reportGunStatus(read)
      return
    }
    // The app's answer about itself: the tablet's ID for Settings, what its camera reads, and
    // which commands it knows. A 2.21 app is told Keep the Screen Awake each time it answers,
    // since a restarted app starts with no wish from the page.
    if (read.kind === 'capabilities') {
      reportTabletInfo(read)
      tellAppKeepScreenOn()
      return
    }
    // The one answer to a camera scan the page asked for: to the asker, never the scan bus.
    if (read.kind === 'camera-scan') {
      settleCameraScan(read)
      return
    }
    // The one answer to a print the page asked for (2.22): to the asker, never the scan bus.
    if (read.kind === 'print') {
      settlePrint(read)
      return
    }
    // A side button opens its job (utils/sideKeys.ts). Through the router, so
    // the screens' own rules for leaving (the forks clear) and the log-in and
    // warehouse checks all apply exactly as for a tap.
    if (read.kind === 'key') {
      const target = sideKeyTarget(read.button, router.currentRoute.value.path)
      if (target) void router.push(target)
      return
    }
    emitScan(read.code)
  })

  // The gun's settings are this app's to decide (gunFeedback.ts). Sent now
  // and on every return to the foreground: the tablet app persists them, so a
  // send it was not up for — a reboot, where the page can be first — is
  // repaired by the next one rather than lost.
  // How much work is on this tablet and not yet with the server. The app
  // shows it outside the site — on its diagnostics screen and notification —
  // for whoever is about to wipe, swap or reset the tablet, any of which
  // destroys this page's storage and the work in it. Told on every change,
  // and again whenever the page comes forward, so a report the app missed is
  // repaired by the next one.
  const tellUnsent = () => {
    postToDevice({
      command: 'pending',
      count: String(queue.pending.value),
      attention: String(queue.needsAttention.value),
    })
  }
  watch([queue.pending, queue.needsAttention], tellUnsent)
  void queue.refreshCounts().then(tellUnsent)

  applyGunSettings()
  askGun()
  askWifi()
  askTabletInfo()
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      applyGunSettings()
      askGun()
      askWifi()
      askTabletInfo()
      tellUnsent()
    }
  })
})
